import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { 
  ArrowLeft, 
  Send, 
  User, 
  Package, 
  DollarSign, 
  Clock,
  MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatPage() {
  const { bidId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [bid, setBid] = useState(null);
  const [listing, setListing] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (bidId) {
      fetchChatData();
      connectWebSocket();
      // Keep a slow poll as a safety-net (every 20s) in case WS drops silently
      pollIntervalRef.current = setInterval(fetchMessages, 20000);
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (err) {
          console.warn('WebSocket close failed:', err);
        }
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidId]);

  const connectWebSocket = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !bidId) return;
      const wsBase = API_URL.replace(/^http/, 'ws');
      const ws = new WebSocket(`${wsBase}/api/ws/chat/${bidId}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onerror = () => setConnected(false);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'message' && data.message) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
          }
        } catch (err) {
          // Non-JSON frames (e.g. "pong") are expected
          console.debug('WS non-JSON frame:', err?.message);
        }
      };
    } catch (err) {
      console.warn('WebSocket setup failed, falling back to polling:', err);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatData = async () => {
    try {
      // Fetch bid details
      const bidsRes = await axios.get(`${API_URL}/api/bids/listing/${bidId.split('-')[0]}`);
      const foundBid = bidsRes.data.find(b => b.id === bidId);
      
      if (!foundBid) {
        // Try to fetch from all user's bids
        const myBidsRes = await axios.get(`${API_URL}/api/bids/my-bids`);
        const myBid = myBidsRes.data.find(b => b.id === bidId);
        if (myBid) {
          setBid(myBid);
          await fetchRelatedData(myBid);
        } else {
          toast.error('Bid not found');
          navigate('/my-bids');
          return;
        }
      } else {
        setBid(foundBid);
        await fetchRelatedData(foundBid);
      }

      await fetchMessages();
    } catch (error) {
      console.error('Failed to fetch chat data', error);
      toast.error('Failed to load chat');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedData = async (bidData) => {
    try {
      // Fetch listing
      const listingRes = await axios.get(`${API_URL}/api/listings/${bidData.listing_id}`);
      setListing(listingRes.data);

      // Determine other user (seller or bidder)
      const otherUserId = user.id === bidData.bidder_id 
        ? listingRes.data.user_id 
        : bidData.bidder_id;
      
      const userRes = await axios.get(`${API_URL}/api/users/${otherUserId}`);
      setOtherUser(userRes.data);
    } catch (error) {
      console.error('Failed to fetch related data', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/bid/${bidId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/messages`, {
        bid_id: bidId,
        content: newMessage.trim()
      });
      setNewMessage('');
      // Optimistic append (also covered by WS broadcast). Dedupe via id.
      if (res?.data?.id) {
        setMessages((prev) => prev.some((m) => m.id === res.data.id) ? prev : [...prev, res.data]);
      } else {
        await fetchMessages();
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' }) + ' ' + 
             date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
           date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: 'bg-blue-100 text-blue-700',
      countered: 'bg-amber-100 text-amber-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      withdrawn: 'bg-slate-100 text-slate-700',
      not_selected: 'bg-slate-100 text-slate-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const isSeller = listing && user.id === listing.user_id;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            
            <div className="flex-1 flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">
                  {otherUser?.name?.[0]?.toUpperCase() || '?'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">
                  {otherUser?.name || 'Loading...'}
                </p>
                <p className="text-xs text-slate-500 flex items-center gap-2">
                  <span>{isSeller ? 'Bidder' : 'Seller'} • {otherUser?.city || 'Unknown location'}</span>
                  <span
                    className={`inline-flex items-center gap-1 ${connected ? 'text-emerald-600' : 'text-slate-400'}`}
                    data-testid="ws-status"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
                    {connected ? 'Live' : 'Polling'}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bid Summary Card */}
      {bid && listing && (
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <Link 
              to={`/listing/${listing.id}`}
              className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
            >
              {listing.images?.[0] ? (
                <img 
                  src={listing.images[0]} 
                  alt={listing.title}
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-slate-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-slate-900 truncate">{listing.title}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(bid.status)}`}>
                    {bid.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-600">
                  {bid.items?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Package className="w-4 h-4" />
                      {bid.items.length} item{bid.items.length > 1 ? 's' : ''}
                    </span>
                  )}
                  {bid.money_amount > 0 && (
                    <span className="flex items-center gap-1 text-emerald-600">
                      <DollarSign className="w-4 h-4" />
                      ${bid.money_amount}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-2">No messages yet</p>
              <p className="text-sm text-slate-400">Start the conversation!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, index) => {
                const isMe = msg.sender_id === user.id;
                const showDate = index === 0 || 
                  new Date(msg.created_at).toDateString() !== 
                  new Date(messages[index - 1].created_at).toDateString();

                return (
                  <React.Fragment key={msg.id}>
                    {showDate && (
                      <div className="text-center">
                        <span className="text-xs text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                          {new Date(msg.created_at).toLocaleDateString([], { 
                            weekday: 'long', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                    )}
                    <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div 
                        className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                          isMe 
                            ? 'bg-indigo-600 text-white rounded-br-md' 
                            : 'bg-white border border-slate-200 text-slate-900 rounded-bl-md'
                        }`}
                      >
                        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isMe ? 'text-indigo-200' : 'text-slate-400'}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-slate-200 sticky bottom-0">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <form onSubmit={handleSendMessage} className="flex items-center gap-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              data-testid="message-input"
            />
            <Button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="px-4 py-3 bg-indigo-600 hover:bg-indigo-700 rounded-xl"
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
